import { useMemo, type FC } from 'react';
import type { FAQInputTypes, ItemType } from './types';
import tinycolor from 'tinycolor2';

import styled from 'styled-components';
import {
  defaultWebValue,
  filterBackgroundColor,
  sanitizeRichText,
  generateAccessKey
} from 'shared/helpers/index';
import { useWebsiteEditor } from 'shared/hooks/index';
import useSales from 'shared/hooks/useSales';

type TitleProps = FAQInputTypes & { backgroundColor?: string; borderColor?: string };

const Title = styled.div<TitleProps>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 24px 32px;
  border: 2px solid;
  transition: background 0.3s ease;
  border-bottom: ${(props) => (props.last ? '1px solid' : 'none')};
  border-radius: ${(props) =>
    props.first
      ? '10px 10px 0 0'
      : props.last && props.isOpen
        ? '0px'
        : props.last
          ? '0px 0px 10px 10px'
          : '0px'};
  border-color: ${(props) => props.borderColor || '#e5e7eb'};
  background-color: ${(props) => props.backgroundColor || '#fff'};

  &:hover {
    cursor: pointer;
    background-color: ${(props) => props.borderColor || '#e5e7eb'} !important;
    transition: background 0.2s ease;
  }
`;

const Question = styled.h3<{ isEmpty?: boolean; editable?: boolean }>`
  font-size: ${({ theme: { $subheadlineFont } }) => $subheadlineFont};
  font-weight: 500;
  width: 90%;
  line-height: 1.35em;
  ${({ isEmpty, editable }) =>
    isEmpty
      ? `
    display: ${editable ? 'block' : 'none'};
    &:not(:focus):before {
      content: attr(data-placeholder);
      cursor: text;
      color: #aaa;
      position: absolute;
      display: flex;
      justify-content: center;
      align-items: center;
    }
  `
      : `
    &:before {
      content: none;
    } 
  `}
`;

const Image = styled.img<Partial<FAQInputTypes>>`
  width: 20px;
  height: 11px;
  transform: ${(props) => (props.isOpen ? 'rotate(180deg)' : 'rotate(0deg)')};
  transition: transform 0.3s ease;
`;

const Answer = styled.p<Partial<FAQInputTypes>>`
  border-left: 2px solid;
  border-right: 2px solid;
  font-size: ${({ theme: { $tinyTextFont } }) => $tinyTextFont};
  padding: 22px 32px;
  line-height: 1.4em;
  border-radius: ${(props) => (props.last ? '0px 0px 10px 10px' : '0px')};
  border-bottom: ${(props) => (props.last ? '1px solid' : 'none')};
  border-color: #e5e7eb;
  ${({ isEmpty, editable }) =>
    isEmpty
      ? `
    display: ${editable ? 'block' : 'none'};
    &:not(:focus):before {
      content: attr(data-placeholder);
      cursor: text;
      color: #aaa;
      position: absolute;
      display: flex;
      justify-content: center;
      align-items: center;
    }
  `
      : `
    &:before {
      content: none;
    } 
  `}
`;

export const FirstItem: FC<ItemType> = (props) => {
  const {
    title = defaultWebValue,
    content = defaultWebValue,
    first,
    last,
    isOpen,
    onClick,
    textColor,
    meta,
    isContentEmpty,
    isTitleEmpty,
    customBackgroundColor,
    oppositeBackgroundColor,
    order,
    index
  } = props;

  const { updateActiveElement, resetActiveElement, editable, websiteAssembler } =
    useWebsiteEditor();

  const { isDashboard } = useSales();

  const bgColor = useMemo(() => {
    if (meta['background_color'] === 'background_color4') {
      return '#fff';
    }
    return tinycolor(oppositeBackgroundColor || '#fff')
      .lighten(3)
      .toHexString();
  }, [oppositeBackgroundColor, meta]);

  const resolvedBorderColor = useMemo(() => {
    const darkenColor = tinycolor(oppositeBackgroundColor || '#e6e6e6')
      .darken(5)
      .toHexString();

    return darkenColor;
  }, [oppositeBackgroundColor]);

  const finalTextColor = useMemo(
    () => filterBackgroundColor(oppositeBackgroundColor || '#f4f4f4', textColor),
    [oppositeBackgroundColor, textColor]
  );

  const answerTextColor = useMemo(
    () => filterBackgroundColor(bgColor || '#e6e6e6', textColor),
    [bgColor, textColor]
  );

  // Accessibility improvements
  const questionAccessKey = generateAccessKey(`q ${index + 1}`, order);
  const answerAccessKey = generateAccessKey(`a ${index + 1}`, order);
  const faqId = `faq-${meta['accessorKey'] || 'item'}-${order}`;
  const answerId = `${faqId}-answer`;

  const handleFAQError = (error: Error | unknown) => {
    // Announce error to screen readers
    const errorAnnouncement = document.createElement('div');
    errorAnnouncement.setAttribute('aria-live', 'polite');
    errorAnnouncement.setAttribute('aria-atomic', 'true');
    errorAnnouncement.className = 'sr-only';
    errorAnnouncement.textContent = `FAQ error: ${error instanceof Error ? error.message : 'Action failed'}`;
    document.body.appendChild(errorAnnouncement);
    setTimeout(() => document.body.removeChild(errorAnnouncement), 1000);
  };

  const handleClick = () => {
    try {
      if (onClick) onClick();
    } catch (error) {
      handleFAQError(error);
    }
  };

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (process.env['APP_TYPE'] === 'dashboard') return;
    // Handle Enter and Space key for accessibility
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      handleClick();
    }
  };

  return (
    <div
      style={{
        display: isContentEmpty && isTitleEmpty && !websiteAssembler ? 'none' : 'block',
        backgroundColor: customBackgroundColor || '#fff'
      }}
      role="group"
      aria-label={`FAQ item ${index + 1}`}>
      <Title
        backgroundColor={
          meta['background_color'] === 'background_color4' ? '#fff' : oppositeBackgroundColor
        }
        borderColor={
          meta['background_color'] === 'background_color4' ? '#d9d9d9' : resolvedBorderColor
        }
        first={first}
        last={last}
        onClick={handleClick}
        onKeyDown={handleKeyDown}
        isOpen={isOpen}
        role="button"
        aria-expanded={isOpen}
        aria-controls={answerId}
        aria-label={`FAQ question: ${title.val ? title.val.replace(/<[^>]*>/g, '') : 'Question'}`}
        tabIndex={0}
        id={faqId}>
        <Question
          id={title.id}
          dangerouslySetInnerHTML={{
            __html: sanitizeRichText(title.val, process.env['APP_TYPE'] === 'dashboard')
          }}
          style={{ color: finalTextColor }}
          isEmpty={isTitleEmpty}
          {...(isDashboard
            ? {
                onClick: (e: React.MouseEvent<HTMLElement>) => {
                  e.stopPropagation();
                  try {
                    updateActiveElement(e);
                  } catch (error) {
                    handleFAQError(error);
                  }
                },
                onBlur: resetActiveElement,
                'data-accessor-parent': meta['accessorKey'],
                'data-accessorkey': 'title',
                'data-benefits': true,
                'data-placeholder': '+',
                editable: true
              }
            : {
                'aria-label':
                  isTitleEmpty || !title.val?.trim() ? `FAQ question ${index + 1}` : undefined,
                accessKey: questionAccessKey,
                title: `Press Alt+${questionAccessKey} to focus this FAQ question`,
                tabIndex: -1 // Remove from tab order as parent Title handles focus
              })}
        />

        <div
          role="img"
          aria-label={isOpen ? 'Collapse answer' : 'Expand answer'}
          aria-hidden="false">
          <Image
            isOpen={isOpen}
            src="/img/arrow-faq.svg"
            alt="trigger section"
            aria-hidden="true"
          />
        </div>
      </Title>

      {isOpen ? (
        <Answer
          id={content.id}
          dangerouslySetInnerHTML={{ __html: sanitizeRichText(content.val, editable) }}
          style={{
            color: answerTextColor,
            borderColor:
              meta['background_color'] === 'background_color4' ? '#d9d9d9' : resolvedBorderColor,
            backgroundColor:
              meta['background_color'] === 'background_color4' || !meta['background_color']
                ? '#f4f4f4'
                : bgColor
          }}
          isEmpty={isContentEmpty}
          role="region"
          aria-labelledby={faqId}
          aria-label="FAQ answer"
          {...(isDashboard
            ? {
                onClick: (e: React.MouseEvent<HTMLElement>) => {
                  e.stopPropagation();
                  try {
                    updateActiveElement(e);
                  } catch (error) {
                    handleFAQError(error);
                  }
                },
                onBlur: resetActiveElement,
                'data-accessor-parent': meta['accessorKey'],
                'data-accessorkey': 'content',
                'data-benefits': true,
                'data-placeholder': '+',
                editable: true
              }
            : {
                'aria-label':
                  isContentEmpty || !content.val?.trim() ? `FAQ answer ${index + 1}` : undefined,
                accessKey: answerAccessKey,
                title: `Press Alt+${answerAccessKey} to focus this FAQ answer`,
                tabIndex: 0
              })}
        />
      ) : null}
    </div>
  );
};

FirstItem.displayName = 'FAQItem';
